import React from 'react';
import {
  string,
  func,
  oneOf,
  bool,
  number,
  any,
} from 'prop-types';
import { TextInput as NativeTextInput } from 'react-native';
import { BrandColor } from '@styleseat/brand';
import './TextInput.module.scss';

/**
 * A wrapper around the react-native `TextInput` which has an API that is more similar to `input`.
 * Depending on your needs, `TextField` or `Textarea` may be more useful.
 */
const TextInput = React.forwardRef(({
  value,
  onChangeText,
  id,
  onBlur,
  onFocus,
  placeholder,
  autoFocus,
  name,
  disabled,
  type,
  className,
  style,
  accentColor,
  appearance,
  lines,
  minInputHeight = 70,
  lineLimit,
  grow,
  multiline,
  characterLimit,
  ...restProps
}, ref) => {
  // transform `type` to `keyboardType`, `secureTextEntry`
  let keyboardType;
  let secureTextEntry = false;
  switch (type) {
    case 'email':
      keyboardType = 'email-address';
      break;
    case 'number':
      keyboardType = 'numeric';
      break;
    case 'url':
      keyboardType = 'url';
      break;
    case 'tel':
    // same as phone
    // eslint-disable-next-line no-fallthrough
    case 'phone':
    case 'shortcode':
      keyboardType = 'phone-pad';
      break;
    case 'search':
      keyboardType = 'web-search';
      break;
    case 'password':
      secureTextEntry = true;
      keyboardType = 'default';
      break;
    default:
      keyboardType = 'default';
      break;
  }

  // if `multiline` is false, we ignore lines, lineLimit, grow
  let numberOfLines = lines;
  if (!multiline) {
    numberOfLines = 1;
  }

  const [height, setHeight] = React.useState(minInputHeight);
  let handleSizeChange = () => { };

  if (multiline && grow) {
    handleSizeChange = event => {
      if (event.nativeEvent.contentSize.height < minInputHeight) {
        setHeight(minInputHeight);
      } else {
        setHeight(event.nativeEvent.contentSize.height);
      }
    };
  }

  return (
    <NativeTextInput
      ref={ref}
      styleName={`kill-resets input input--${appearance}`}
      style={[
        style,
        {
          ...(multiline && { height }),
          borderColor: accentColor,
          ...(disabled && {
            backgroundColor: BrandColor.LightGrey,
            color: BrandColor.Metal,
          }),
        },
      ]}
      className={className}
      onContentSizeChange={handleSizeChange}
      onChangeText={onChangeText}
      onFocus={onFocus}
      onBlur={onBlur}
      value={value}
      autoFocus={autoFocus}
      nativeID={id}
      name={name}
      placeholder={placeholder}
      editable={!disabled}
      disabled={disabled}
      keyboardType={keyboardType}
      secureTextEntry={secureTextEntry}
      placeholderTextColor={BrandColor.Silver}
      multiline={multiline}
      numberOfLines={numberOfLines}
      maxLength={characterLimit}
      {...restProps}
    />
  );
});

export const TextInputPropTypes = {
  /** Current value of the field */
  value: string.isRequired,
  /** Called when the text changes, receiving the text value */
  onChangeText: func,
  /** ID of the field */
  id: string,
  /** Field type */
  type: oneOf([
    'text',
    'search',
    'tel',
    'url',
    'email',
    'password',
    'phone',
    'shortcode',
    'number',
  ]),
  /** Called when focus is lost */
  onBlur: func,
  /** Called when focus is gained */
  onFocus: func,
  /** Placeholder text */
  placeholder: string,
  /** Field name */
  name: string,
  /** Color of borders and message text */
  accentColor: string,
  /** True to disable the field, otherwise false */
  disabled: bool,
  /** True to autofocus the field, otherwise false */
  autoFocus: bool,
  /** Limit on the number of characters allowed */
  characterLimit: number,
  /** The number of lines to display initially */
  lines: number,
  /** The minimum height (px) this input should shrink to */
  minInputHeight: number,
  /** The maximum number of lines */
  lineLimit: number,
  /** True to grow as text changes */
  grow: bool,
  /** True to act like a textarea */
  multiline: bool,
  /** Additional styling for the input component */
  style: any,
  /** Additional styling for the input component */
  className: string,
  /**
   * - `bordered` (default) uses a bordered textarea
   * - `stretched` to indicate there should be no margin from container edges or border
   * - `stretched-aligned` is similar to stretched, except it also removes left and right padding
   *        in the textarea so that the placeholder is aligned with the label
   */
  appearance: oneOf([
    'stretched',
    'bordered',
    'stretch-aligned',
    'form',
  ]),

  // These props are suggested by the braid design system
  // If you need to expand props to support one of these types of functionality,
  // please one of the props below
  // /** Called when the clear button is clicked */
  // onClear: func,
  // /** An icon to show on the left side of the input, used only when multiline is false */
  // icon: node,
  // /** Called (after onChange) when content is pasted rather than typed into the field*/
  // onPaste: func,
};

TextInput.propTypes = TextInputPropTypes;

export const TextInputDefaultProps = {
  id: undefined,
  type: 'text',
  onBlur: null,
  onFocus: null,
  placeholder: null,
  name: null,
  accentColor: BrandColor.Silver,
  disabled: false,
  autoFocus: false,
  characterLimit: undefined,
  lines: undefined,
  minInputHeight: 70,
  lineLimit: undefined,
  grow: false,
  multiline: false,
  style: null,
  className: '',
  appearance: 'bordered',
  onChangeText: () => { },
};

TextInput.defaultProps = TextInputDefaultProps;

export default TextInput;
